import { ChevronDown, Star } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import useTranslation from "../hooks/useTranslation";

interface Strategy {
  id: string;
  name: string;
  color: string;
}

interface StrategyMultiSelectProps {
  strategies: Strategy[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  className?: string;
  pinnedIds?: string[];
  onPinIds?: (ids: string[]) => void;
}

export default function StrategyMultiSelect({
  strategies,
  selectedIds,
  onChange,
  className,
  pinnedIds,
  onPinIds,
}: StrategyMultiSelectProps) {
  const t = useTranslation();

  const selectedSet = new Set(selectedIds);

  const toggle = (id: string) => {
    onChange(
      selectedSet.has(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id]
    );
  };

  const selectedStrategies = strategies.filter((s) => selectedSet.has(s.id));

  const isActive = selectedIds.length > 0;

  return (
    <div className={cn(className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={isActive ? "default" : "outline"}
            className={cn(
              "h-9 w-full justify-between font-medium text-sm min-w-0",
              isActive && "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
            )}
          >
            <span className="truncate">
              {selectedStrategies.length === 0
                ? t.journal.allStrategies
                : t.journal.strategiesSelected.replace("{n}", String(selectedStrategies.length))}
            </span>
            <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          {strategies.map((s) => {
            const isPinned = pinnedIds?.includes(s.id);
            return (
              <DropdownMenuCheckboxItem
                key={s.id}
                checked={selectedSet.has(s.id)}
                onCheckedChange={() => toggle(s.id)}
                className="pr-1"
              >
                <span className="flex items-center gap-2 flex-1 min-w-0">
                  <span
                    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="truncate">{s.name}</span>
                </span>
                {onPinIds && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPinIds([s.id]);
                    }}
                    className={cn(
                      "ml-2 flex-shrink-0 flex h-5 w-5 items-center justify-center rounded transition-colors",
                      isPinned ? "text-yellow-400" : "text-muted-foreground/30 hover:text-yellow-400"
                    )}
                  >
                    <Star className={cn("h-3 w-3", isPinned && "fill-yellow-400")} />
                  </button>
                )}
              </DropdownMenuCheckboxItem>
            );
          })}
          {selectedIds.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onChange([])}
                className="text-muted-foreground text-xs"
              >
                {t.journal.clearStrategies}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

    </div>
  );
}
